import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ExportJobTracker, AsyncJobState, JobStatus } from '../../services/export-job-tracker.service';

/**
 * Status Pill Async
 * ------------------
 * Una pill por cada job que el `ExportJobTracker` esté trackeando. Sigue
 * exactamente el patrón visual de las demás Status Pills de la Command Bar
 * (`.status-pill` + `.pulse-dot` + animación pulse), añadiendo solo la
 * variante `.exporting` (verde neón — color reservado a progreso/éxito en el
 * sistema de diseño).
 *
 * Click sobre la pill → popover compacto con detalle + botón "Cancelar".
 *
 * @author Marcos Cordo
 * @version 06/06/2026_1
 */
@Component({
  selector: 'app-status-pill-async',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './status-pill-async.component.html',
  styleUrls: ['./status-pill-async.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusPillAsyncComponent {
  private tracker = inject(ExportJobTracker);

  /** Jobs vivos o recién finalizados — el host los olvida al mostrar Toast. */
  jobs = this.tracker.jobs;

  /** jobId del popover abierto, o null si ninguno. */
  openedJobId = signal<string | null>(null);

  /** Estado del job cuyo popover está abierto. */
  openedJob = computed<AsyncJobState | null>(() => {
    const id = this.openedJobId();
    if (!id) return null;
    return this.jobs().find(j => j.jobId === id) ?? null;
  });

  /** Variante CSS según el estado (mapea a las clases ya existentes en el sistema). */
  variantFor(status: JobStatus): string {
    switch (status) {
      case 'queued':
      case 'running':
        return 'exporting';
      case 'partial':
        return 'warning';
      case 'success':
        return 'exporting';      // se desvanece al forget() del host
      case 'cancelled':
        return 'warning';
      case 'failed':
        return 'danger';
    }
  }

  /** Texto principal de la pill. */
  labelFor(job: AsyncJobState): string {
    switch (job.status) {
      case 'queued':
        return `${job.exporterLabel.toUpperCase()} EN COLA`;
      case 'running': {
        const pct = Math.round(job.progress * 100);
        return `EXPORTANDO A ${job.exporterLabel.toUpperCase()} ${pct}%`;
      }
      case 'partial':
        return `${job.exporterLabel.toUpperCase()} PARCIAL`;
      case 'success':
        return `${job.exporterLabel.toUpperCase()} OK`;
      case 'cancelled':
        return `${job.exporterLabel.toUpperCase()} CANCELADO`;
      case 'failed':
        return `${job.exporterLabel.toUpperCase()} FALLÓ`;
    }
  }

  togglePopover(jobId: string): void {
    this.openedJobId.update(prev => prev === jobId ? null : jobId);
  }

  closePopover(): void {
    this.openedJobId.set(null);
  }

  isTerminal(status: JobStatus): boolean {
    return status === 'success' || status === 'failed' || status === 'cancelled';
  }

  cancelJob(jobId: string): void {
    void this.tracker.requestCancel(jobId);
    this.closePopover();
  }

  /** trackBy para *ngFor: estabiliza el DOM entre cambios del Signal jobs(). */
  trackByJobId(_index: number, job: AsyncJobState): string {
    return job.jobId;
  }
}